// @boty/ai - Content gap analysis for clarification questions

import {
  getOpenAIClient,
  AI_MODELS,
  AIProviderError,
  retryDelay,
  handleOpenAIError,
} from './provider';
import type {
  ContentAnalysisResult,
  ContentAnalysisOptions,
  ClarificationQuestion,
  AIContentAnalysisResponse,
} from './types';

/**
 * Maximum content length before truncation (100K chars as per story spec)
 */
const MAX_CONTENT_LENGTH = 100000;

/**
 * Maximum retries for API calls
 */
const MAX_RETRIES = 3;

/**
 * System prompt for content gap analysis
 */
const CONTENT_ANALYSIS_PROMPT = `You are an expert content analyst for a chatbot creation platform.
Your task is to identify information gaps and generate clarification questions.

IMPORTANT: Generate your questions, explanations, and all text in the same language as the content provided. If the content is in Hebrew, respond in Hebrew. If in Spanish, respond in Spanish. Match the content's language exactly.

QUESTION GENERATION RULES:
1. Generate exactly the number of questions requested — no more, no less. Every question must have non-empty "question" and "explanation" text.
2. Assign priority: "high" (critical gaps), "medium" (helpful gaps), "low" (nice-to-have)
3. Focus on MISSING information, not improvements
4. Each question should target ONE specific piece of missing info
5. Avoid generic questions like "Can you tell me more?"
6. Consider what an attendee/customer would commonly ask

CATEGORIES TO CHECK:
- contact_info: Email, phone, address, social media
- pricing: Costs, fees, packages, discounts
- schedule: Hours, dates, availability, deadlines
- services: What you offer, features, capabilities
- policies: Returns, cancellations, refunds, terms
- location: Address, service areas, delivery zones
- credentials: Qualifications, certifications, experience
- process: How things work, steps, procedures
- other: Anything else important

Return ONLY valid JSON in this format:
{
  "questions": [
    {
      "question": "What is your email address or phone number for inquiries?",
      "explanation": "Contact information is not included in your content, which visitors commonly need.",
      "category": "contact_info",
      "priority": "high"
    }
  ],
  "topicsIdentified": ["consulting services", "training programs"],
  "gapsFound": 4
}`;

/**
 * Truncates content if it exceeds the maximum length
 * Adds a note at the end if truncated
 */
function truncateContent(content: string): string {
  if (content.length <= MAX_CONTENT_LENGTH) {
    return content;
  }

  const truncated = content.slice(0, MAX_CONTENT_LENGTH);
  return truncated + '\n\n[Content truncated for analysis...]';
}

/**
 * Parses and validates the AI response
 */
function parseAIResponse(
  responseContent: string | null,
  botId: string
): ContentAnalysisResult {
  if (!responseContent) {
    throw new AIProviderError('PARSE_ERROR', 'Empty response from AI');
  }

  let parsed: AIContentAnalysisResponse;

  try {
    parsed = JSON.parse(responseContent);
  } catch {
    throw new AIProviderError('PARSE_ERROR', 'Invalid JSON response from AI');
  }

  // Transform questions to full ClarificationQuestion objects.
  // Drop entries where the AI returned an empty/missing question text — they render
  // as "ghost" cards in the UI showing only the explanation fallback.
  const questions: ClarificationQuestion[] = (parsed.questions || [])
    .filter((q) => typeof q?.question === 'string' && q.question.trim().length > 0)
    .map((q) => ({
      id: crypto.randomUUID(),
      botId,
      question: q.question.trim(),
      explanation: q.explanation || 'This information would help your bot provide better answers.',
      category: q.category || 'other',
      priority: q.priority || 'medium',
      status: 'pending' as const,
      createdAt: new Date().toISOString(),
    }));

  return {
    questions,
    topicsIdentified: parsed.topicsIdentified || [],
    gapsFound: parsed.gapsFound || questions.length,
  };
}

/**
 * Analyzes content to identify information gaps and generate clarification questions
 *
 * @param content - The combined bot content to analyze
 * @param options - Analysis options including botId
 * @returns Analysis result with questions and topics identified
 * @throws AIProviderError on API failures
 *
 * @example
 * ```typescript
 * const result = await analyzeContentGaps('Our company offers consulting...', {
 *   botId: 'bot-123',
 *   maxQuestions: 5,
 * });
 * console.log(result.questions.length); // 5
 * ```
 */
export async function analyzeContentGaps(
  content: string,
  options: ContentAnalysisOptions
): Promise<ContentAnalysisResult> {
  const { botId, maxQuestions = 5, onUsage } = options;

  // Handle empty content
  if (!content || content.trim().length === 0) {
    return {
      questions: [],
      topicsIdentified: [],
      gapsFound: 0,
    };
  }

  const truncatedContent = truncateContent(content);
  const client = getOpenAIClient();

  for (let attempt = 0; attempt < MAX_RETRIES; attempt++) {
    try {
      const startTime = Date.now();
      const response = await client.chat.completions.create({
        model: AI_MODELS.GPT4O_MINI,
        messages: [
          { role: 'system', content: CONTENT_ANALYSIS_PROMPT },
          {
            role: 'user',
            content: `Analyze this content and generate exactly ${maxQuestions} clarification questions:\n\n${truncatedContent}`,
          },
        ],
        response_format: { type: 'json_object' },
        temperature: 0.7, // Some creativity in questions
        max_tokens: 1500,
      });

      const messageContent = response.choices[0]?.message?.content ?? null;
      const result = parseAIResponse(messageContent, botId);

      if (onUsage) {
        try {
          onUsage({
            model: AI_MODELS.GPT4O_MINI,
            provider: 'openai',
            inputTokens: response.usage?.prompt_tokens ?? 0,
            outputTokens: response.usage?.completion_tokens ?? 0,
            latencyMs: Date.now() - startTime,
            functionName: 'analyzeContentGaps',
            inputMessages: [
              { role: 'system', content: CONTENT_ANALYSIS_PROMPT.slice(0, 500) },
              { role: 'user', content: truncatedContent.slice(0, 1000) },
            ],
            ...(messageContent && { outputText: messageContent.slice(0, 1000) }),
          });
        } catch { /* never break the call */ }
      }

      return result;
    } catch (error) {
      // Re-throw AIProviderError directly (e.g., from parseAIResponse)
      if (error instanceof AIProviderError) {
        // Retry on rate limits and timeouts
        if (
          (error.code === 'RATE_LIMIT_EXCEEDED' || error.code === 'TIMEOUT') &&
          attempt < MAX_RETRIES - 1
        ) {
          await retryDelay(attempt);
          continue;
        }
        throw error;
      }

      // Handle OpenAI API errors
      handleOpenAIError(error, 'Content Analysis');
    }
  }

  // Should not reach here
  throw new AIProviderError('API_ERROR', 'Max retries exceeded for content analysis');
}
